import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faEnvelope, faUser } from "@fortawesome/pro-regular-svg-icons";

import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { Icon } from "@/components/Icon/Icon";
import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { ALL_SENDERS_ITEM, normalizeSenderSelection } from "../utils/normalizeSenderSelection.util";

import type { CampaignNodeTypes } from "../config/node-types";

import type { Sender } from "./Sender";

const CHANNEL_NODE_TYPES: CampaignNodeTypes[] = [
	"emailNode",
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
];

const SENDER_NODE_TYPES: CampaignNodeTypes[] = [
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
	"linkedinVisitNode",
];

export interface NodePanelHeaderProps {
	level?: number | string;
	nodeType: CampaignNodeTypes;
	senders?: Sender[];
	selectedSenders?: DropdownWithCheckboxesItem[];
	/** Locks the channel and sender pills, e.g. when the step has already been sent. */
	disabled?: boolean;
	onChangeNodeType: (nodeType: CampaignNodeTypes) => void;
	onChangeSenders: (senders: DropdownWithCheckboxesItem[]) => void;
}

/**
 * NodePanelHeader — header row of the campaign node panel.
 *
 * Renders the step label next to two compact meta pills (channel + sender),
 * mirroring the campaign-builder mockup.
 */
export const NodePanelHeader: FunctionComponent<NodePanelHeaderProps> = ({
	level,
	nodeType,
	senders,
	selectedSenders,
	disabled,
	onChangeNodeType,
	onChangeSenders,
}) => {
	const { t } = useTranslation();

	const channelOptions: AutoCompleteItem[] = useMemo(
		() => [
			{
				id: "emailNode",
				title: t("campaign.nodePanel.channels.email"),
				icon: <Icon className="w-3.5 h-3.5" icon={faEnvelope} />,
			},
			{
				id: "linkedinNode",
				title: t("campaign.nodePanel.channels.linkedinMessage"),
				icon: <BrandsIcon className="w-3.5 h-3.5" icon={faLinkedinIn as IconDefinition} />,
			},
			{
				id: "linkedinInviteNode",
				title: t("campaign.nodePanel.channels.linkedinInvite"),
				icon: <BrandsIcon className="w-3.5 h-3.5" icon={faLinkedinIn as IconDefinition} />,
			},
			{
				id: "linkedinInmailNode",
				title: t("campaign.nodePanel.channels.linkedinInmail"),
				icon: <BrandsIcon className="w-3.5 h-3.5" icon={faLinkedinIn as IconDefinition} />,
			},
		],
		[t]
	);

	const selectedChannel = useMemo(
		() => channelOptions.find((option) => option.id === nodeType),
		[channelOptions, nodeType]
	);

	const senderOptions: DropdownWithCheckboxesItem[] = useMemo(
		() => [
			ALL_SENDERS_ITEM,
			...(senders
				?.filter((sender) => sender.active && sender.type === "linkedin")
				?.map((sender) => ({ id: sender.userId, title: sender.name })) ?? []),
		],
		[senders]
	);

	const channelIcon =
		nodeType === "emailNode" ? (
			<Icon className="w-3.5 h-3.5" icon={faEnvelope} />
		) : (
			<BrandsIcon className="w-3.5 h-3.5" icon={faLinkedinIn as IconDefinition} />
		);

	return (
		<div className="flex flex-row flex-wrap items-center gap-2 mb-4">
			<div className="text-ssm font-semibold whitespace-nowrap">
				{t("campaign.nodePanel.step", { level })}
			</div>

			{CHANNEL_NODE_TYPES.includes(nodeType) && (
				<Dropdown
					data={channelOptions}
					defaultValue={selectedChannel}
					disabled={disabled}
					floating={true}
					handleSelect={(value) => onChangeNodeType(value.id as CampaignNodeTypes)}
					icon={channelIcon}
					variant="pill"
				/>
			)}
			{nodeType === "linkedinVisitNode" && (
				<div className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-lg border border-border-light bg-brand-white px-2 py-[5px] text-xs font-medium text-text-meta">
					<span className="flex items-center text-gray-700">{channelIcon}</span>
					{t("campaign.nodePanel.visitProfile")}
				</div>
			)}

			{SENDER_NODE_TYPES.includes(nodeType) && (
				<DropdownWithCheckboxes
					data={senderOptions}
					defaultValue={[ALL_SENDERS_ITEM]}
					disabled={disabled}
					floating={true}
					handleSelect={(value) =>
						onChangeSenders(normalizeSenderSelection(value, selectedSenders))
					}
					icon={<Icon className="w-3.5 h-3.5" icon={faUser} />}
					title="campaign.nodePanel.sender"
					value={selectedSenders}
					variant="pill"
				/>
			)}
		</div>
	);
};
